import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import { SectionItem } from './types';

interface BlueprintVideoCardProps {
  item: SectionItem;
  onPlay?: (item: SectionItem) => void;
}

const BlueprintVideoCard = ({ item, onPlay }: BlueprintVideoCardProps) => {
  const appsUsed = item.metadata?.appsUsed || [];
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Videos,
  });

  return (
    <div className="group flex flex-col overflow-hidden">
      <div className="relative aspect-[5/3] w-full overflow-hidden">
        <button
          type="button"
          onClick={() => onPlay?.(item)}
          onClickCapture={() =>
            trackEvent(
              click({
                clickTarget: ClickTarget.Card,
                clickText: item.title || undefined,
                contentId: item.id,
              })
            )
          }
          className="absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent p-0"
        >
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt={item.title || ''}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
            <div className="size-56 flex items-center justify-center rounded-full bg-white/90">
              <svg className="size-24 ml-4 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </button>
      </div>
      <div className="flex flex-col gap-8 bg-[#F0ECE580] p-16">
        <div className="flex flex-col gap-4">
          {item.label && (
            <span className="font-suisse-intl-mono text-overline-md text-primary-magenta">
              {item.label}
            </span>
          )}
          {item.title && (
            <h3 className="m-0 font-suisse-neue text-subheadline-sm text-grayscale-charcoal">
              {item.title}
            </h3>
          )}
          {appsUsed.length > 0 && (
            <span className="font-suisse-screen text-byline-sm text-grayscale-charcoal underline">
              Apps used
            </span>
          )}
        </div>
        {appsUsed.length > 0 && (
          <div className="flex gap-8">
            {appsUsed.map((app) => (
              <div
                key={app.name}
                className="flex size-[42px] items-center justify-center rounded-10 bg-white"
              >
                <img src={app.iconUrl} alt={app.name} className="size-[28px] object-contain" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlueprintVideoCard;
